/**
 * Fill-in-the-blank starter templates for the offering wizard.
 *
 * Templates are built from whatever the teacher has already selected on the
 * Targeting step (subject, grade band, curriculum, offering type). Because
 * Targeting comes before Details, most placeholders are already resolved with
 * real values by the time the teacher reaches the Details step.
 *
 * Design intent for descriptions:
 *   Each description answers three questions a student/parent has in the first
 *   ten seconds: "Is this for me?", "What actually happens in a session?",
 *   "Why should I trust this teacher?"
 *
 * Keep language direct and specific. Vague promises ("you will improve") are
 * filtered out by savvy parents — concrete descriptions ("we solve one
 * past-paper question set per chapter, every session") build trust.
 */

export interface TemplateContext {
  subjectName?: string;
  gradeLabel?: string;
  curriculumLabel?: string;
  offeringType?: string;
}

const ph = (v: string | undefined, fallback: string) => (v && v.trim() ? v : fallback);

export interface OfferingTemplate {
  label: string;
  description: string;
  title: string;
  descriptionHtml: string;
  outcomesHtml: string;
  prerequisitesHtml: string;
}

/** One-click templates that fill title, description, outcomes, and prerequisites together. */
export function fullTemplates(ctx: TemplateContext): OfferingTemplate[] {
  const subject = ph(ctx.subjectName, "[Subject]");
  const grade   = ph(ctx.gradeLabel, "[Grade]");
  const board   = ph(ctx.curriculumLabel, "[Board]");

  const templates: OfferingTemplate[] = [

    // ── Exam prep ─────────────────────────────────────────────────────────────
    {
      label: "Exam prep",
      description: "For students who know the content but lose marks in the exam",
      title: `${grade} ${board} ${subject} — exam-focused problem solving`,
      descriptionHtml:
        // Who this is for
        `<p><strong>Who this is for.</strong> ${grade} ${board} students who have covered the ${subject} syllabus but lose marks on application questions, long-form answers, or time management in the exam. If you understand the concepts but your test scores don't show it, this class is for you.</p>` +

        // What happens
        `<p><strong>What happens in each session.</strong> We work through ${board}-style questions together — not theory lectures. Each session: one topic, past-paper problems solved step by step, then a short drill you do independently while I watch for mistakes. I note every error pattern and we target them directly in the next session.</p>` +

        // Between sessions
        `<p><strong>Between sessions.</strong> You get a focused practice sheet — usually 8–12 questions on exactly what we covered. No busywork. I review your answers before the next session so we don't repeat the same mistakes twice.</p>` +

        // Why me — teacher fills this
        `<p>[Replace this paragraph: share one specific result or credential that gives a student/parent confidence. E.g. "I have taught ${board} ${subject} for 6 years. In my last batch of 12 students, 9 scored above 85% in their board exam."]</p>`,

      outcomesHtml:
        `<ul>` +
        `<li>Work through ${board} ${subject} past-paper questions chapter by chapter — not just a final revision dump</li>` +
        `<li>Complete a full exam paper within the time limit, with correct method and step-by-step working</li>` +
        `<li>Build a personal error log so you stop repeating the same mistakes</li>` +
        `<li>Reach consistent accuracy on the question types that carry the most marks</li>` +
        `<li>[Add a measurable target if you have one, e.g. "hit 80%+ on the last 3 mock papers before your exam"]</li>` +
        `</ul>`,

      prerequisitesHtml:
        `<ul>` +
        `<li>You have covered the ${grade} ${board} ${subject} syllabus at least once — at school or with another tutor</li>` +
        `<li>Bring your ${subject} textbook, a notebook for working, and at least one past paper to the first session</li>` +
        `<li>[Add anything specific to your class, e.g. "a scientific calculator" or "access to the official mark scheme" — or delete this line]</li>` +
        `</ul>`,
    },

    // ── Build basics ──────────────────────────────────────────────────────────
    {
      label: "Build basics",
      description: "For students who find the subject confusing and want to start fresh",
      title: `${subject} for ${grade} — build real understanding from scratch`,
      descriptionHtml:
        // Who this is for
        `<p><strong>Who this is for.</strong> ${grade} students who find ${subject} genuinely difficult — not because they aren't trying, but because something in the foundation was never properly explained. If you've been told to "just memorise the formula" and it isn't working, this class is for you.</p>` +

        // What happens
        `<p><strong>How sessions work.</strong> We slow down and build properly. Every concept gets a plain-English explanation first, then a real-world example, then we practise together until it feels natural — before moving on. There are no silly questions. If something isn't clear, we find a different way to explain it.</p>` +

        // Progress tracking
        `<p><strong>Tracking your progress.</strong> At the end of every session I give you 3–5 practice questions to try on your own. We review them together at the start of the next session. You will see your confidence grow week by week.</p>` +

        // Why me — teacher fills this
        `<p>[Replace this paragraph: describe your approach to students who struggle. E.g. "I spent 4 years teaching ${subject} remedial classes at a school in [City]. Most of my students went from failing grades to a solid pass within one term."]</p>`,

      outcomesHtml:
        `<ul>` +
        `<li>Understand the foundational concepts of ${subject} — not just the steps, but the reason behind them</li>` +
        `<li>Solve standard ${board} ${grade} problems independently, without needing to look up the method</li>` +
        `<li>Build the confidence to attempt questions you'd have skipped before</li>` +
        `<li>Develop a consistent study habit with short, focused practice between sessions</li>` +
        `<li>[Add one outcome specific to your teaching style, e.g. "stop dreading ${subject} and start seeing it as manageable"]</li>` +
        `</ul>`,

      prerequisitesHtml:
        `<ul>` +
        `<li>No prior preparation needed — we find out exactly where to start in the first session</li>` +
        `<li>Bring your school ${subject} textbook and a notebook; we'll use the textbook as our guide</li>` +
        `<li>Be ready to ask questions — this class only works if you speak up when something isn't clear</li>` +
        `</ul>`,
    },
  ];

  // ── Crash course ─────────────────────────────────────────────────────────────
  if (ctx.offeringType === "CRASH_COURSE") {
    templates.unshift({
      label: "Crash course",
      description: "Intensive revision in the weeks before the exam — highest-weightage topics first",
      title: `${board} ${subject} crash course — ${grade} exam sprint`,
      descriptionHtml:
        // Who this is for + urgency
        `<p><strong>Who this is for.</strong> ${grade} ${board} students whose ${subject} exam is coming up in the next few weeks. The syllabus is done — now the goal is to maximise marks in the time left. Every session in this course is designed around the exam, not the textbook.</p>` +

        // Method
        `<p><strong>How the course runs.</strong> We triage the syllabus on day one: highest-weightage chapters first, weaker topics next, everything else as time allows. Each session: rapid chapter recap → past-paper questions solved under exam conditions → mark-scheme walkthrough so you see exactly where marks are gained and lost. A daily revision checklist keeps you on track between sessions.</p>` +

        // Mock test + results
        `<p><strong>Final mock test.</strong> The last session is a full timed mock paper. We mark it together and you leave with a clear picture of where you stand and what to focus on in the final days before the exam.</p>` +

        // Track record — teacher fills this
        `<p>[Replace this paragraph with your track record. Be specific: e.g. "In my last ${board} ${subject} crash batch (${grade}), 7 out of 9 students improved their mock score by at least 15 marks. Two students went from a predicted D to a B."]</p>`,

      outcomesHtml:
        `<ul>` +
        `<li>Cover all high-weightage ${board} ${subject} chapters in priority order — not randomly</li>` +
        `<li>Practise past-paper questions for every chapter covered, reviewed against the mark scheme</li>` +
        `<li>Sit a full timed mock exam and know exactly how to interpret your result</li>` +
        `<li>Leave every session with a specific list of topics to review before the next one</li>` +
        `<li>Walk into the exam with a strategy — which questions to attempt first, how to manage time</li>` +
        `<li>[Add a score target if you're confident one is realistic, e.g. "aim for 75%+ on the final mock"]</li>` +
        `</ul>`,

      prerequisitesHtml:
        `<ul>` +
        `<li>You have finished the ${board} ${subject} syllabus at least once — even if shakily</li>` +
        `<li>Bring at least two past papers to the first session; we will use them throughout the course</li>` +
        `<li>Be prepared to put in 1–2 hours of revision between sessions — the course only works with practice outside class too</li>` +
        `<li>[Add anything specific, e.g. "a scientific calculator" or "the official ${board} formula sheet" — or delete this line]</li>` +
        `</ul>`,
    });
  }

  // ── Group classes ─────────────────────────────────────────────────────────────
  if (ctx.offeringType === "GROUP") {
    templates.unshift({
      label: "Group classes",
      description: "Regular classes for a small batch — syllabus covered together, doubts cleared in class",
      title: `${grade} ${board} ${subject} — small group classes`,
      descriptionHtml:
        // Who this is for
        `<p><strong>Who this is for.</strong> ${grade} students following the ${board} ${subject} syllabus who want regular, structured teaching without paying for one-to-one sessions. The batch moves through the syllabus together, so you stay consistent and learn from each other's questions as well as your own.</p>` +

        // Session structure
        `<p><strong>How each class runs.</strong> Every class has three parts: new topic taught with examples (30–40 min) → in-class practice problems solved together (15–20 min) → open doubt round where any question from the week is fair game (10 min). The batch size stays small — [add your max batch size, e.g. "no more than 8 students"] — so you always get individual attention, not just a seat in a virtual classroom.</p>` +

        // Schedule + logistics
        `<p>[Replace this paragraph with your schedule and logistics. E.g. "Classes run every Tuesday and Thursday, 6:00–7:30 pm IST. All sessions are recorded and shared with the batch within 24 hours. A shared notes document is updated after every class."]</p>` +

        // Why me — teacher fills this
        `<p>[Replace this paragraph with one line about your experience. E.g. "I have been teaching ${board} ${subject} for [X] years and have run [N] successful batches. References available on request."]</p>`,

      outcomesHtml:
        `<ul>` +
        `<li>Cover the entire ${board} ${subject} ${grade} syllabus in a structured, chapter-by-chapter sequence</li>` +
        `<li>Practise problems in every class — not just at home alone</li>` +
        `<li>Get doubts cleared in real time, every session — no backlog of unanswered questions</li>` +
        `<li>Stay consistent: the batch schedule keeps you accountable week to week</li>` +
        `<li>[Add one batch-specific outcome, e.g. "monthly progress test so you always know where you stand"]</li>` +
        `</ul>`,

      prerequisitesHtml:
        `<ul>` +
        `<li>The batch follows the syllabus from the beginning — no prior preparation needed to join</li>` +
        `<li>Keep your ${board} ${subject} textbook ready; we follow it chapter by chapter</li>` +
        `<li>Attend consistently — missing sessions means falling behind the batch</li>` +
        `<li>[Add any joining condition, e.g. "the batch is currently on Chapter 4 — new joiners should review Chapters 1–3 before their first class" — or delete this line]</li>` +
        `</ul>`,
    });
  }

  return templates;
}

export function titleTemplates(ctx: TemplateContext): string[] {
  const subject = ph(ctx.subjectName, "[Subject]");
  const grade   = ph(ctx.gradeLabel, "[Grade]");
  const board   = ph(ctx.curriculumLabel, "[Board]");

  const base = [
    `${grade} ${board} ${subject} — exam-focused problem solving`,
    `${subject} for ${grade} — build real understanding from scratch`,
    `${grade} ${board} ${subject} — personalised one-to-one sessions`,
    `${board} ${subject} (${grade}) — weekly sessions, strong fundamentals`,
  ];

  if (ctx.offeringType === "CRASH_COURSE") {
    base.unshift(`${board} ${subject} crash course — ${grade} exam sprint`);
  } else if (ctx.offeringType === "GROUP") {
    base.unshift(`${grade} ${board} ${subject} — small group classes`);
  }

  return base.slice(0, 3);
}

export function descriptionTemplates(ctx: TemplateContext): { label: string; html: string }[] {
  const subject = ph(ctx.subjectName, "[Subject]");
  const grade   = ph(ctx.gradeLabel, "[Grade]");
  const board   = ph(ctx.curriculumLabel, "[Board]");

  return [
    {
      label: "Exam prep",
      html:
        `<p><strong>Who this is for.</strong> ${grade} ${board} students who have covered the ${subject} syllabus but lose marks on application questions or exam technique.</p>` +
        `<p><strong>What happens.</strong> Each session: one topic, past-paper questions solved together, mistakes noted and targeted next time. You get a focused practice sheet between sessions.</p>` +
        `<p>[Add one line about your experience or previous students' results.]</p>`,
    },
    {
      label: "Build basics",
      html:
        `<p><strong>Who this is for.</strong> ${grade} students who find ${subject} genuinely difficult and want to build real understanding — not just memorise formulas.</p>` +
        `<p><strong>How sessions work.</strong> Plain-English explanation first, real example second, then practice together until it clicks — before we move on. Questions are always welcome.</p>` +
        `<p>[Add one line about your teaching approach or what makes your sessions different.]</p>`,
    },
  ];
}

export function outcomesTemplates(ctx: TemplateContext): { label: string; html: string }[] {
  const subject = ph(ctx.subjectName, "[Subject]");
  const board   = ph(ctx.curriculumLabel, "[Board]");

  return [
    {
      label: "Exam outcomes",
      html:
        `<ul>` +
        `<li>Work through ${board} ${subject} past-paper questions chapter by chapter</li>` +
        `<li>Complete a full exam paper within the time limit with correct step-by-step working</li>` +
        `<li>Build a personal error log and stop repeating the same mistakes</li>` +
        `<li>[Add a measurable target, e.g. "hit 80%+ on the last 3 mock papers before your exam"]</li>` +
        `</ul>`,
    },
    {
      label: "Concept outcomes",
      html:
        `<ul>` +
        `<li>Understand the foundational ${subject} concepts — not just the steps, but the reason behind them</li>` +
        `<li>Solve standard ${board} problems independently, without needing to look up the method</li>` +
        `<li>Build confidence to attempt questions you'd have skipped before</li>` +
        `<li>[Add one outcome specific to your course]</li>` +
        `</ul>`,
    },
  ];
}
